import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Footer from "./components/Footer";
import Modal from "./components/Modal";
import Navbar from "./components/Navbar";
import PropertyTypeSelector from "./components/PropertyTypeSelector";
import ProtectedRoute from "./components/ProtectedRoute";
import { applySavedTheme } from "./components/ThemeSelector";
import { useAuth } from "./context/AuthContext";
import AddProperty from "./pages/AddProperty";
import AdminQueue from "./pages/AdminQueue";
import AccountActivity from "./pages/AccountActivity";
import AccountHome from "./pages/AccountHome";
import AccountListings from "./pages/AccountListings";
import AccountProfile from "./pages/AccountProfile";
import ChooseRole from "./pages/ChooseRole";
import Home from "./pages/Home";
import Listings from "./pages/Listings";
import Login from "./pages/Login";
import PostPropertyStart from "./pages/PostPropertyStart";
import PropertyDetails from "./pages/PropertyDetails";
import Register from "./pages/Register";

function RootRedirect() {
  const { loading, isAuthenticated, role } = useAuth();

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (role === "admin") {
    return <Navigate to="/admin/queue" replace />;
  }

  return <Navigate to="/listings" replace />;
}

function AuthEntryRoute({ children }) {
  const location = useLocation();
  const { loading, isAuthenticated, role } = useAuth();

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  if (!isAuthenticated) {
    return children;
  }

  const dashboardRoute = role === "admin" ? "/admin/queue" : "/account";

  if (location.state?.openPostProperty) {
    return (
      <Navigate
        to={dashboardRoute}
        replace
        state={{
          openPostProperty: true,
          selectedType: location.state?.selectedType,
          listingType: location.state?.listingType,
          postPropertySource: "dashboard",
          message: location.state?.message || "Welcome back. Continue completing your property details."
        }}
      />
    );
  }

  return <Navigate to={dashboardRoute} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
}

function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const routesWithoutGlobalChrome = new Set();
  const [postPropertyState, setPostPropertyState] = useState({
    open: false,
    selectedType: null,
    listingType: "sale",
    source: "site",
    message: ""
  });
  const isDashboardRoute =
    location.pathname.startsWith("/account") ||
    location.pathname.startsWith("/admin") ||
    location.pathname === "/post-property" ||
    location.pathname === "/add" ||
    location.pathname === "/choose-role";
  const hideGlobalChrome = routesWithoutGlobalChrome.has(location.pathname);
  const showFooter = !hideGlobalChrome && !isDashboardRoute;

  useEffect(() => {
    applySavedTheme();
  }, []);

  useEffect(() => {
    const openPostProperty = (event) => {
      if (!isAuthenticated) {
        return;
      }

      setPostPropertyState({
        open: true,
        selectedType: event?.detail?.selectedType || null,
        listingType: event?.detail?.listingType === "rent" ? "rent" : "sale",
        source: event?.detail?.source || "site",
        message: event?.detail?.message || ""
      });
    };

    window.addEventListener("fy-open-post-property", openPostProperty);
    return () => window.removeEventListener("fy-open-post-property", openPostProperty);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !location.state?.openPostProperty) {
      return;
    }

    setPostPropertyState({
      open: true,
      selectedType: location.state?.selectedType || null,
      listingType: location.state?.listingType === "rent" ? "rent" : "sale",
      source: location.state?.postPropertySource || "site",
      message: location.state?.message || ""
    });

    const nextState = { ...(location.state || {}) };
    delete nextState.openPostProperty;
    delete nextState.selectedType;
    delete nextState.listingType;
    delete nextState.postPropertySource;
    delete nextState.message;
    delete nextState.onboarding;

    navigate(location.pathname, {
      replace: true,
      state: Object.keys(nextState).length > 0 ? nextState : null
    });
  }, [isAuthenticated, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (location.state?.openPostProperty) {
      return;
    }

    setPostPropertyState({
      open: false,
      selectedType: null,
      listingType: "sale",
      source: "site",
      message: ""
    });
  }, [location.pathname, location.state?.openPostProperty]);

  const onSelectPostType = (type) => {
    setPostPropertyState((prev) => ({
      ...prev,
      open: true,
      selectedType: type
    }));
  };

  const closePostPropertyModal = () => {
    setPostPropertyState({
      open: false,
      selectedType: null,
      listingType: "sale",
      source: "site",
      message: ""
    });
  };

  const onPostPropertySuccess = (_created, message) => {
    window.dispatchEvent(new CustomEvent("fy-property-created", { detail: { message } }));
    const nextRoute =
      postPropertyState.source === "dashboard" || location.pathname.startsWith("/account") || location.pathname.startsWith("/admin")
        ? "/account/listings"
        : "/listings";

    closePostPropertyModal();
    navigate(nextRoute, { state: { message } });
  };

  const postPropertyTitle = postPropertyState.listingType === "rent" ? "🔑 Rent" : "🏷 Sell";
  const postPropertyDescription =
    postPropertyState.listingType === "rent"
      ? "Select the type of rental property you want to post."
      : "Select the type of property you want to post.";

  return (
    <div className="app-shell">
      {!hideGlobalChrome ? <Navbar /> : null}
      <main className="app-main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/buy" element={<Home />} />
          <Route path="/rent" element={<Home />} />
          <Route path="/sell" element={<Home />} />
          <Route path="/services" element={<Home />} />
          <Route path="/home-loans" element={<Home />} />
          <Route
            path="/login"
            element={(
              <AuthEntryRoute>
                <Login />
              </AuthEntryRoute>
            )}
          />
          <Route
            path="/register"
            element={(
              <AuthEntryRoute>
                <Register />
              </AuthEntryRoute>
            )}
          />
          <Route path="/post-property" element={<PostPropertyStart />} />

          <Route
            path="/choose-role"
            element={
              <ProtectedRoute>
                <ChooseRole />
              </ProtectedRoute>
            }
          />
          <Route
            path="/listings"
            element={<Listings />}
          />
          <Route
            path="/property/:id"
            element={<PropertyDetails />}
          />
          <Route
            path="/add"
            element={
              <ProtectedRoute allowedRoles={["common", "agent", "builder", "admin"]}>
                <AddProperty />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/queue"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminQueue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute allowedRoles={["common", "agent", "builder", "admin"]}>
                <AccountHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/profile"
            element={
              <ProtectedRoute allowedRoles={["common", "agent", "builder", "admin"]}>
                <AccountProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/activity"
            element={
              <ProtectedRoute allowedRoles={["common", "agent", "builder", "admin"]}>
                <AccountActivity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/listings"
            element={
              <ProtectedRoute allowedRoles={["common", "agent", "builder", "admin"]}>
                <AccountListings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/orders"
            element={<Navigate to="/account/listings" replace />}
          />

          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </main>
      {showFooter ? <Footer /> : null}
      {postPropertyState.open ? (
        <Modal
          className={postPropertyState.selectedType ? "fy-modal--wide" : "fy-modal--compact"}
          onClose={closePostPropertyModal}
        >
          {postPropertyState.selectedType ? (
            <AddProperty
              embedded
              onClose={closePostPropertyModal}
              onSuccess={onPostPropertySuccess}
              initialPrefill={{
                type: postPropertyState.selectedType,
                listing_type: postPropertyState.listingType
              }}
              initialMessage={postPropertyState.message || "Property type selected. Complete the listing form below."}
            />
          ) : (
            <PropertyTypeSelector
              title={postPropertyTitle}
              description={postPropertyDescription}
              onClose={closePostPropertyModal}
              onSelect={onSelectPostType}
            />
          )}
        </Modal>
      ) : null}
    </div>
  );
}

export default App;
